import { Box, Stack, TableCell, Typography } from '@mui/material'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../../config/navigation'
import { RequestProps } from '../../../store/types'
import LocalStorageTypes from '../../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../../store/types/enums/SessionStorageKeys'
import Cost from '../../../store/types/models/Cost'
import { Column, Row } from '../../../store/types/utils/DataTableUtils'
import { formatStringDate } from '../../../store/utils/dateUtils'
import usePersistedPage from '../../../store/utils/usePersistedPage'
import { costTypeOptions } from '../../Sidebar/cost/CostViewForm'
import DataTable from '../DataTable'

type ProjectCostsTableProps = {
  costs: Cost[]
  isLoading: boolean
  totalElements: number
  totalCost: number
  setRequestProps: Dispatch<SetStateAction<RequestProps>>
}

const ProjectCostsTable = ({ costs, isLoading, totalElements, totalCost, setRequestProps }: ProjectCostsTableProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = usePersistedPage(SessionStorageKeys.ProjectCostsTablePage)

  const columns: Column[] = [
    { id: 'total', label: t('page.costSummary.table.header.total') },
    { id: 'amount', label: t('page.costSummary.table.header.amount') },
    { id: 'quantity', label: t('page.costSummary.table.header.quantity') },
    { id: 'type', label: t('page.costSummary.table.header.type') },
    { id: 'author', label: t('page.costSummary.table.header.payer') },
    { id: 'startDate', label: t('page.costSummary.table.header.startingDate') },
    { id: 'endDate', label: t('page.costSummary.table.header.endingDate') },
  ]

  const renderRow = (cost: Row) => {
    return (
      <>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>{`${(cost.amount * cost.quantity).toFixed(2)} zł`}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{`${cost.amount.toFixed(2)} zł`}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{cost.quantity}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {t(
              costTypeOptions.find(option => option.value === cost.type)?.label ||
                'components.costSidebar.types.unknown'
            )}
          </Typography>
        </TableCell>
        <TableCell sx={{ height: 52 }}>
          <Typography
            variant="body2"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={() => navigate(generatePath(navigation.userProfile, { id: cost.author?.id }))}
          >
            {cost.author?.firstName} {cost.author?.lastName}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{cost.startDate !== null ? formatStringDate(cost.startDate) : '-'}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{cost.endDate !== null ? formatStringDate(cost.endDate) : '-'}</Typography>
        </TableCell>
      </>
    )
  }

  const renderTileContent = (cost: Row, labelId: string) => {
    return (
      <Box key={labelId} sx={{ p: 2 }}>
        <Typography variant="body1" fontWeight={500}>
          {`${(cost.amount * cost.quantity).toFixed(2)} zł`}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t(
            costTypeOptions.find(option => option.value === cost.type)?.label || 'components.costSidebar.types.unknown'
          )}
        </Typography>
        <Typography variant="body2">
          {cost.author?.firstName} {cost.author?.lastName}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {cost.startDate} - {cost.endDate}
        </Typography>
      </Box>
    )
  }

  return (
    <Stack gap={{ sm: 2 }} height={'100%'}>
      <Typography variant="body2" fontWeight={500}>
        {t('page.projects.detailsView.totalCost')}: {totalCost.toFixed(2)} zł
      </Typography>
      <Box sx={{ flex: 1, height: '100%' }}>
        <DataTable
          renderTileContent={renderTileContent}
          singleSelection={false}
          columns={columns}
          data={costs}
          renderRow={renderRow}
          totalElements={totalElements}
          setRequestProps={setRequestProps}
          translationForNotFound={t('page.costSummary.table.notFound')}
          page={page}
          setPage={setPage}
          isLoading={isLoading}
          removeCheckboxes
          hoverOff
          isLastCellNormal
          commonRowsPerPage={
            localStorage.getItem(LocalStorageTypes.CostSummaryRows)
              ? parseInt(localStorage.getItem(LocalStorageTypes.CostSummaryRows)!)
              : undefined
          }
          localStorageRowsName={LocalStorageTypes.CostSummaryRows}
        />
      </Box>
    </Stack>
  )
}

export default ProjectCostsTable
